<script lang="ts">
  import { onMount } from 'svelte';
  import { internalLink } from '$lib/utils/internalLink';
  import { contactsScrollPosition, restoreContactsScroll } from '$lib/utils/contactScroll';
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { peopleApi } from '$lib/api/crm';
  import { touchpointsApi } from '$lib/api/touchpoints';
  import { apiFetch, getErrorMessage } from '$lib/api/client';
  import { route } from '$lib/state/route.svelte';
  import { session } from '$lib/state/session.svelte';
  import { confirmAction } from '$lib/state/actionDialog.svelte';
  import { csvCell, contactDetailsBody, contactCompanyInfo, contactNextStep, relativeDay, shortDay, plural, sentenceCase, touchpointTypeLabel } from '$lib/utils/touchpoints';
  import { relationshipLabels, initials } from '$lib/utils/salesHome';
  import { beginContactCall, consumeContactCall } from '$lib/utils/pendingCall';
  import type { Person } from '$lib/types';
  import Composer from './Composer.svelte';
  import Pagination from '$lib/components/ui/Pagination.svelte';
  import CompanyCombobox from '$lib/components/ui/CompanyCombobox.svelte';
  import CustomFieldsForm from '$lib/components/ui/CustomFieldsForm.svelte';

  // Note: tests evaluate this script with only the dependencies it needs for
  // bulk actions, so top-level code must not call display-only imports.
  const client = useQueryClient();
  const vertical = $derived(session.selectedVertical === 'all' ? undefined : session.selectedVertical);
  const filters = $derived(route.current.tab === 'people' ? route.current.filters ?? {} : {});
  const view = $derived(filters.view ?? (session.me?.user.intake_coordinator || session.isAdmin ? 'unassigned' : 'needs_attention'));
  const page = $derived(Number(filters.page) || 1);
  const views = [['needs_attention', 'Needs attention'], ['mine', 'My contacts'], ['week', 'Due this week'], ['unassigned', 'No owner'], ['still_with_me', 'Still with me'], ['all', 'All']];
  const ownerId = $derived(filters.owner_user_id || (view === 'needs_attention' && !session.isAdmin && !session.me?.user.intake_coordinator ? 'me' : undefined));
  // company_label only names the company chip; it is never sent to the API.
  const scopeFilters = $derived({ ...filters, last_contact_days: filters.last_contact_days ?? filters.quiet_days, quiet_days: undefined, company_label: undefined });
  const list = createQuery(() => ({
    queryKey: ['people', vertical, filters, view, ownerId],
    queryFn: async () => {
      const data = await peopleApi.list({ ...scopeFilters, owner_id: ownerId, view, sort: filters.sort || (view === 'needs_attention' ? 'next_step' : 'recent'), vertical_id: vertical, page, per_page: 30 });
      return { ...data, results: data.results.map(p => ({ ...p, company_name: p.company_name || contactCompanyInfo(p)?.name })) };
    }
  }));
  const counts = createQuery(() => ({
    queryKey: ['people-view-counts', vertical, filters, ownerId],
    queryFn: () => touchpointsApi.viewCounts({ ...scopeFilters, view: undefined, page: undefined, sort: undefined, owner_id: ownerId, vertical_id: vertical })
  }));
  const owners = createQuery(() => ({ queryKey: ['contact-owners', 'sales'], queryFn: () => peopleApi.salesOwners() }));

  let drawer = $state(false), selected = $state<number[]>([]), composer = $state(false), contact = $state<Person | null>(null), initialType = $state('call'), mode = $state<'log' | 'plan'>('log'), restoreScroll = $state<number | null>(null);
  let creating = $state(false), error = $state(''), busy = $state(false), bulk = $state(''), bulkOwner = $state<number>(), bulkNote = $state(''), bulkDate = $state('');
  let form = $state({ first_name: '', last_name: '', phone: '', email: '', whatsapp: '', city: '', job_title: '', relationship_type: '', notes: '', company_id: undefined as number | undefined, hotel_company_id: undefined as number | undefined, brand_company_id: undefined as number | undefined, owner_company_id: undefined as number | undefined, custom: {} as Record<string, unknown>, vertical_id: 0 });
  const hotel = $derived(session.me?.verticals.find(v => v.id === form.vertical_id)?.slug === 'hotels');
  const storage = $derived(`contacts-return:${session.me?.user.id}:${vertical ?? 'all'}`);
  const canAssign = $derived(session.isAdmin || !!session.me?.user.intake_coordinator);

  function setFilters(values: Record<string, string>) {
    const next: Record<string, string> = { ...filters, ...values, page: '1' };
    for (const key of Object.keys(values)) if (!values[key]) delete next[key];
    route.replace({ tab: 'people', filters: next });
    selected = [];
  }
  function setFilter(key: string, value: string) {
    setFilters(key === 'company_id' && !value ? { company_id: '', company_label: '' } : { [key]: value });
  }
  function rememberList() {
    try { sessionStorage.setItem(storage, JSON.stringify({ filters, scroll: contactsScrollPosition() })); } catch { /* Storage may be blocked. */ }
  }
  function open(p: Person) { rememberList(); route.push({ tab: 'people', id: p.id, filters }); }
  onMount(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(storage) ?? 'null');
      if (saved) {
        if (!Object.keys(filters).length && saved.filters) route.replace({ tab: 'people', filters: saved.filters });
        restoreScroll = saved.scroll ?? 0;
      }
    } catch { /* Storage may be blocked. */ }
  });
  $effect(() => {
    if (list.isSuccess && restoreScroll !== null) {
      const y = restoreScroll;
      restoreScroll = null;
      requestAnimationFrame(() => restoreContactsScroll(y));
    }
  });
  $effect(() => {
    const ids = list.data?.results.map(p => p.id);
    if (ids && selected.some(id => !ids.includes(id))) selected = selected.filter(id => ids.includes(id));
  });
  function log(p: Person, type = 'call') { contact = p; initialType = type; mode = 'log'; composer = true; }
  function keyboard(e: KeyboardEvent) {
    if (composer || creating || (e.target as HTMLElement)?.closest('input,textarea,select,[contenteditable=true],[data-tym-modal]') || e.ctrlKey || e.metaKey || e.altKey) return;
    if (['l', 's'].includes(e.key.toLowerCase())) {
      contact = null;
      mode = e.key.toLowerCase() === 's' ? 'plan' : 'log';
      initialType = mode === 'plan' ? 'meeting' : 'call';
      composer = true;
    }
  }
  function returned() {
    const call = consumeContactCall();
    if (!call) return;
    const p = list.data?.results.find(p => p.id === call.personId);
    if (p) log(p, call.type);
    else route.push({ tab: 'people', id: call.personId });
  }
  $effect(() => {
    const signal = session.consumeSignal('people');
    if (signal?.mode === 'create') { form.vertical_id = vertical ?? session.me?.verticals[0]?.id ?? 0; creating = true; }
  });
  function startCreate() {
    form.vertical_id = vertical ?? session.me?.verticals[0]?.id ?? 0;
    creating = !creating;
  }
  async function saveContact() {
    if (busy || !form.first_name.trim() || !form.vertical_id) return;
    busy = true;
    error = '';
    try {
      await peopleApi.create({ ...contactDetailsBody(form), vertical_id: form.vertical_id });
      creating = false;
      await client.invalidateQueries();
    } catch (err) {
      error = getErrorMessage(err);
    } finally {
      busy = false;
    }
  }
  async function remove(p: Person) {
    if (!await confirmAction(`Delete ${p.first_name} ${p.last_name}? Linked work: ${plural(p.open_deals ?? 0, 'open deal')}, ${plural(p.open_cases ?? 0, 'open case')}; touchpoint history may remain linked.`)) return;
    try { await peopleApi.remove(p.id); await client.invalidateQueries(); } catch (err) { error = getErrorMessage(err); }
  }
  function nextFor(p: Person) { return contactNextStep(p.next_followup, p.next_plan); }
  function exportRows() {
    const rows = (list.data?.results ?? []).filter(p => selected.includes(p.id));
    const csv = [
      ['Name', 'Phone', 'Email', 'Company', 'Contact type', 'Owner', 'Next step', 'Next step date'],
      ...rows.map(p => { const n = nextFor(p); return [`${p.first_name} ${p.last_name}`, p.phone, p.email, p.company_name, relationshipLabels[p.relationship_type ?? ''] ?? p.relationship_type, p.contact_owner_name, n?.title, n?.day]; })
    ].map(row => row.map(csvCell).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'selected-contacts.csv';
    a.click();
    URL.revokeObjectURL(url);
  }
  async function bulkSave() {
    if (busy || !bulkNote.trim() || (bulk === 'assign' && !bulkOwner) || (bulk === 'followup' && !bulkDate)) return;
    busy = true;
    const failed: string[] = [];
    const succeeded: number[] = [];
    for (const p of (list.data?.results ?? []).filter(p => selected.includes(p.id))) {
      try {
        if (bulk === 'assign') {
          const owner = owners.data?.results.find(o => o.id === bulkOwner);
          if (!owner) throw new Error('Owner not eligible');
          const destination = owner.vertical_id ?? p.vertical_id;
          // A handover into another pipeline must take the open deals along.
          await peopleApi.transfer(p.id, { owner_user_id: owner.id, vertical_id: destination, note: bulkNote.trim(), move_open_deals: destination !== p.vertical_id });
        } else {
          const eligible = await client.fetchQuery({ queryKey: ['activity-assignees', p.vertical_id], queryFn: () => apiFetch<Array<{ id: number; name: string }>>(`/activities/assignees?vertical_id=${p.vertical_id}`) });
          if (!p.owner_user_id || !eligible.some(u => u.id === p.owner_user_id)) throw new Error('Assign an eligible contact owner before planning a follow-up.');
          const when = new Date(`${bulkDate}T09:00:00+05:30`);
          if (!(when.getTime() > Date.now())) throw new Error('Choose a future follow-up date (09:00 IST).');
          await touchpointsApi.write('/activities', { person_id: p.id, vertical_id: p.vertical_id, company_id: p.company_id, type: 'task', status: 'scheduled', title: bulkNote.trim().split('\n')[0], body: bulkNote.trim(), scheduled_at: when.toISOString(), occurred_at: null, next_action: '', next_action_date: null, no_followup: false, assignee_user_id: p.owner_user_id });
        }
        succeeded.push(p.id);
      } catch (err) {
        failed.push(`${p.first_name} ${p.last_name}: ${getErrorMessage(err)}`);
      }
    }
    selected = selected.filter(id => !succeeded.includes(id));
    error = failed.length ? `${succeeded.length} saved; failures: ${failed.join('; ')}` : '';
    busy = false;
    if (!failed.length) bulk = '';
    await client.invalidateQueries();
  }
  function chipLabel(key: string, value: string): string {
    switch (key) {
      case 'owner_user_id': return `Owner: ${owners.data?.results.find(o => String(o.id) === value)?.name ?? 'Selected'}`;
      case 'relationship_type': return `Type: ${relationshipLabels[value] ?? sentenceCase(value)}`;
      case 'company_id': return `Company: ${filters.company_label ?? 'Selected'}`;
      case 'city': return `City: ${value}`;
      case 'next_step': return `Next step: ${nextStepFilters.find(([k]) => k === value)?.[1] ?? value}`;
      case 'has_open_deal': return value === 'true' ? 'Has open deal' : 'No open deal';
      case 'has_open_case': return value === 'true' ? 'Has open case' : 'No open case';
      case 'quiet_days': case 'last_contact_days': return `Quiet for ${value}+ days`;
      case 'sort': return `Sorted by ${sortOptions.find(([k]) => k === value)?.[1].toLowerCase() ?? value}`;
      default: return `${sentenceCase(key)}: ${value}`;
    }
  }
  const nextStepFilters = [['overdue', 'Overdue'], ['today', 'Due today'], ['week', 'Due this week'], ['none', 'None planned']];
  const sortOptions = [['next_step', 'Next step due'], ['last_contact', 'Last contact'], ['name', 'Name'], ['recent', 'Recently added']];
  const fieldLabels = [['first_name', 'First name'], ['last_name', 'Last name'], ['phone', 'Phone'], ['email', 'Email'], ['whatsapp', 'WhatsApp'], ['job_title', 'Job title'], ['city', 'City']];
</script>

<svelte:window onfocus={returned} onkeydown={keyboard} />
<div class="tym-page contacts">
  <div class="topbar">
    <div class="day-segment scroll" role="group" aria-label="Saved views">
      {#each views as [key, label]}
        {#if key !== 'still_with_me' || session.me?.user.intake_coordinator}
          <button type="button" aria-pressed={view === key} onclick={() => setFilter('view', key)}>{label} <span class="count">{counts.data?.[key] ?? '—'}</span></button>
        {/if}
      {/each}
    </div>
    <button type="button" class="btn-primary" onclick={startCreate}>＋ New contact</button>
  </div>
  {#if counts.isError}<p class="tym-error-state" role="alert">View totals unavailable: {getErrorMessage(counts.error)}</p>{/if}

  {#if creating}
    <section class="day-card panel" aria-label="New contact">
      <div class="panel-head"><h2 class="day-title">New contact</h2><button type="button" class="day-mini-btn" onclick={() => creating = false}>Cancel</button></div>
      <div class="form-grid">
        <label class="field">Pipeline
          <select class="input" bind:value={form.vertical_id}>{#each session.me?.verticals ?? [] as v (v.id)}<option value={v.id}>{v.name}</option>{/each}</select>
        </label>
        <label class="field">Contact type
          <select class="input" bind:value={form.relationship_type}>
            <option value="">Choose…</option>
            {#each Object.entries(relationshipLabels).filter(([key]) => key) as [value, text]}<option {value}>{text}</option>{/each}
          </select>
        </label>
        {#each fieldLabels as [key, label]}
          <label class="field">{label}{key === 'first_name' ? ' *' : ''}<input class="input" bind:value={form[key as keyof typeof form] as string} type={key === 'email' ? 'email' : key === 'phone' || key === 'whatsapp' ? 'tel' : 'text'} /></label>
        {/each}
        {#if hotel}
          <label class="field">Hotel<CompanyCombobox verticalId={form.vertical_id} kind="hotel" bind:value={form.hotel_company_id} /></label>
          <label class="field">Brand<CompanyCombobox verticalId={form.vertical_id} kind="brand" bind:value={form.brand_company_id} /></label>
          <label class="field">Group<CompanyCombobox verticalId={form.vertical_id} kind="owner" bind:value={form.owner_company_id} /></label>
        {:else}
          <label class="field">Company<CompanyCombobox verticalId={form.vertical_id} bind:value={form.company_id} /></label>
        {/if}
        <label class="field wide">Notes<textarea class="input" rows="2" bind:value={form.notes}></textarea></label>
      </div>
      <CustomFieldsForm defs={session.customFieldsFor('people', form.vertical_id)} bind:values={form.custom} />
      <div class="row-actions">
        <button type="button" class="btn-primary" disabled={busy || !form.first_name.trim()} onclick={saveContact}>{busy ? 'Saving…' : 'Save contact'}</button>
        <button type="button" class="btn-secondary" onclick={() => creating = false}>Cancel</button>
      </div>
    </section>
  {/if}

  <div class="search">
    <input class="input" type="search" aria-label="Search contacts" value={filters.q ?? ''} oninput={e => setFilter('q', e.currentTarget.value)} placeholder="Search by name, phone, email or company…" />
    <button type="button" class="btn-secondary" aria-expanded={drawer} onclick={() => drawer = !drawer}>Filters</button>
  </div>
  {#if Object.entries(filters).some(([key, value]) => value && !['view', 'page', 'q', 'company_label'].includes(key))}
    <div class="chips">
      {#each Object.entries(filters).filter(([key, value]) => value && !['view', 'page', 'q', 'company_label'].includes(key)) as [key, value]}
        <button type="button" class="tym-chip" onclick={() => setFilter(key, '')} aria-label={`Remove filter ${chipLabel(key, value)}`}>{chipLabel(key, value)} ×</button>
      {/each}
      <button type="button" class="day-link" onclick={() => route.replace({ tab: 'people', filters: { view } })}>Clear all</button>
    </div>
  {/if}

  {#if drawer}
    <section class="day-card panel" aria-label="Contact filters">
      <div class="panel-head"><h2 class="day-title">Filters</h2><button type="button" class="day-mini-btn" onclick={() => drawer = false}>Done</button></div>
      <div class="form-grid three">
        <label class="field">Owner
          <select class="input" value={filters.owner_user_id ?? ''} onchange={e => setFilter('owner_user_id', e.currentTarget.value)}>
            <option value="">All owners</option>
            {#each owners.data?.results ?? [] as owner (owner.id)}<option value={String(owner.id)}>{owner.name}</option>{/each}
          </select>
        </label>
        <label class="field">Contact type
          <select class="input" value={filters.relationship_type ?? ''} onchange={e => setFilter('relationship_type', e.currentTarget.value)}>
            <option value="">All types</option>
            {#each Object.entries(relationshipLabels).filter(([key]) => key) as [value, text]}<option {value}>{text}</option>{/each}
          </select>
        </label>
        <label class="field">Company
          <CompanyCombobox verticalId={vertical} value={filters.company_id ? Number(filters.company_id) : undefined} onSelect={c => setFilters({ company_id: String(c.id), company_label: c.name })} placeholder="Search companies…" />
        </label>
        <label class="field">City<input class="input" value={filters.city ?? ''} onchange={e => setFilter('city', e.currentTarget.value.trim())} placeholder="Any city" /></label>
        <label class="field">Next step
          <select class="input" value={filters.next_step ?? ''} onchange={e => setFilter('next_step', e.currentTarget.value)}>
            <option value="">Any</option>
            {#each nextStepFilters as [key, label]}<option value={key}>{label}</option>{/each}
          </select>
        </label>
        <label class="field">Last contact
          <select class="input" value={filters.quiet_days ?? ''} onchange={e => setFilter('quiet_days', e.currentTarget.value)}>
            <option value="">Any time</option>
            {#each [7, 14, 30, 90] as days}<option value={String(days)}>Over {days} days ago</option>{/each}
          </select>
        </label>
        {#each [['has_open_deal', 'Open deals'], ['has_open_case', 'Open cases']] as [key, label]}
          <label class="field">{label}
            <select class="input" value={filters[key] ?? ''} onchange={e => setFilter(key, e.currentTarget.value)}>
              <option value="">Any</option><option value="true">Has some</option><option value="false">None</option>
            </select>
          </label>
        {/each}
        <label class="field">Sort by
          <select class="input" value={filters.sort ?? (view === 'needs_attention' ? 'next_step' : 'recent')} onchange={e => setFilter('sort', e.currentTarget.value)}>
            {#each sortOptions as [key, label]}<option value={key}>{label}</option>{/each}
          </select>
        </label>
      </div>
    </section>
  {/if}

  {#if selected.length}
    <section class="day-card-dark bulk" aria-label="Bulk actions">
      <div class="bulk-head">
        <strong>{plural(selected.length, 'contact')} selected on this page</strong>
        <div class="row-actions">
          {#if canAssign}<button type="button" class="bulk-btn" aria-pressed={bulk === 'assign'} disabled={busy} onclick={() => bulk = 'assign'}>Assign owner</button>{/if}
          <button type="button" class="bulk-btn" aria-pressed={bulk === 'followup'} disabled={busy} onclick={() => bulk = 'followup'}>Add follow-up</button>
          <button type="button" class="bulk-btn" onclick={exportRows}>Export CSV</button>
          <button type="button" class="bulk-btn" onclick={() => { selected = []; bulk = ''; }}>Clear selection</button>
        </div>
      </div>
      {#if bulk}
        <div class="bulk-form">
          {#if bulk === 'assign'}
            <select class="input" aria-label="New owner" bind:value={bulkOwner} disabled={busy}>
              <option value={undefined}>Choose sales owner</option>
              {#each owners.data?.results ?? [] as owner (owner.id)}<option value={owner.id}>{owner.name}</option>{/each}
            </select>
          {:else}
            <label class="bulk-label">Plan a task for each contact's owner at 09:00 IST on
              <input class="input" aria-label="Follow-up date" type="date" bind:value={bulkDate} disabled={busy} />
            </label>
          {/if}
          <textarea class="input" aria-label="Required note" rows="2" bind:value={bulkNote} disabled={busy} placeholder={bulk === 'assign' ? 'Required handover note. If the new owner sells in another pipeline, open deals move with the contact.' : 'What should the owner do? This creates a plan, never a completed conversation.'}></textarea>
          <button type="button" class="btn-primary" disabled={busy} onclick={bulkSave}>{busy ? 'Saving…' : 'Apply to selected'}</button>
        </div>
      {/if}
    </section>
  {/if}

  {#if error}<p class="tym-error-state" role="alert">{error}</p>{/if}

  {#if list.isPending}
    <p class="tym-loading-state">Loading contacts…</p>
  {:else if list.isError}
    <p class="tym-error-state" role="alert">{getErrorMessage(list.error)} <button type="button" class="day-mini-btn" onclick={() => list.refetch()}>Retry</button></p>
  {:else}
    <div class="tym-table-wrap list">
      <div class="head row" aria-hidden="true"><div>Contact</div><div>Owner</div><div>Last contact</div><div>Next step</div><div>Open work</div><div></div></div>
      {#each list.data?.results ?? [] as p (p.id)}
        {@const n = nextFor(p)}
        {@const last = p.last_touchpoint}
        <article class="row" class:picked={selected.includes(p.id)}>
          <div class="identity">
            <input type="checkbox" aria-label={`Select ${p.first_name} ${p.last_name}`} checked={selected.includes(p.id)} onchange={e => selected = e.currentTarget.checked ? [...selected, p.id] : selected.filter(id => id !== p.id)} />
            <span class="avatar" aria-hidden="true">{initials(p.first_name, p.last_name)}</span>
            <button type="button" class="name" onclick={() => open(p)}>
              <strong>{p.first_name} {p.last_name}</strong>
              {#if p.job_title || p.company_name || p.hotel_name || p.brand_name || p.owner_name || p.city}<small>{[p.job_title, [p.company_name, p.hotel_name, p.brand_name, p.owner_name].filter(Boolean).join(' · ') || p.city].filter(Boolean).join(' · ')}</small>{/if}
              <small class="type">{relationshipLabels[p.relationship_type ?? ''] ?? sentenceCase(p.relationship_type)}{!p.company_id && !p.hotel_company_id && !p.brand_company_id && !p.owner_company_id ? ' · No company linked' : ''}</small>
            </button>
          </div>
          <div class="cell" data-label="Owner">{#if p.owner_user_id}{p.contact_owner_name}{:else}<span class="day-chip day-chip-warn">No owner</span>{/if}</div>
          <div class="cell" data-label="Last contact" title={last?.body}>
            {#if last}<strong>{touchpointTypeLabel(last.type)}</strong> <span class="muted">· {relativeDay(last.occurred_at ?? last.touchpoint_at ?? last.created_at)}</span>{:else}<span class="muted">Never contacted</span>{/if}
          </div>
          <div class="cell" data-label="Next step">
            {#if n}
              <p class="next-title">{n.title}</p>
              <span class="day-chip {n.overdue ? 'day-chip-danger' : n.day && relativeDay(n.day) === 'Today' ? 'day-chip-warn' : 'day-chip-info'}">
                {n.overdue ? 'Overdue · ' : ''}{n.day ? (['Today', 'Tomorrow'].includes(relativeDay(n.day)) ? relativeDay(n.day) : shortDay(n.day)) : 'No date'}{n.time ? `, ${n.time}` : ''}
              </span>
            {:else}
              <span class="muted">{p.no_followup ? 'No follow-up needed' : 'Nothing planned'}</span>
            {/if}
          </div>
          <div class="cell" data-label="Open work">
            {#if (p.open_deals ?? 0) || (p.open_cases ?? 0)}
              <a class="day-link" href={`#/people/${p.id}`} use:internalLink={{ tab: 'people', id: p.id }} onclick={rememberList}>{[p.open_deals ? plural(p.open_deals, 'deal') : '', p.open_cases ? plural(p.open_cases, 'case') : ''].filter(Boolean).join(' · ')}</a>
            {:else}<span class="muted">—</span>{/if}
          </div>
          <div class="actions">
            {#if p.phone}<a class="day-mini-btn" href={`tel:${p.phone}`} onclick={() => beginContactCall(p)}>Call</a>{/if}
            {#if p.whatsapp || p.phone}<a class="day-mini-btn" href={`https://wa.me/${(p.whatsapp || p.phone).replace(/\D/g, '')}`} target="_blank" rel="noreferrer" onclick={() => beginContactCall(p, 'whatsapp')}>WhatsApp</a>{/if}
            <button type="button" class="day-mini-btn log" onclick={() => log(p)}>Log</button>
            <details class="menu">
              <summary class="day-mini-btn" aria-label={`More actions for ${p.first_name}`}>⋯</summary>
              <div class="menu-list">
                <button type="button" onclick={() => open(p)}>Open contact</button>
                <button type="button" class="danger" onclick={() => remove(p)}>Delete {p.first_name}</button>
              </div>
            </details>
          </div>
        </article>
      {:else}
        <div class="empty">
          <h2 class="day-title">{view === 'needs_attention' ? 'Nobody needs attention. Nice.' : 'No contacts match these filters.'}</h2>
          <div class="row-actions">
            <button type="button" class="btn-secondary" onclick={() => route.replace({ tab: 'people', filters: { view: 'all' } })}>Show all contacts</button>
            <button type="button" class="btn-secondary" onclick={() => route.replace({ tab: 'people', filters: { view: 'all', quiet_days: '30' } })}>Quiet for 30+ days</button>
          </div>
        </div>
      {/each}
    </div>
    {#if list.data}<Pagination {page} perPage={30} count={list.data.count} onChange={p => route.replace({ tab: 'people', filters: { ...filters, page: String(p) } })} />{/if}
  {/if}
</div>
{#if composer}<Composer person={contact} {initialType} {mode} onClose={() => composer = false} />{/if}

<style>
  .contacts { display: grid; gap: 14px; }
  .topbar { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; }
  .count { margin-left: 4px; opacity: .7; font-variant-numeric: tabular-nums; }
  .search { display: flex; gap: 10px; }
  .search input { flex: 1; }
  .chips, .row-actions, .actions { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
  .panel { display: grid; gap: 16px; }
  .panel-head { display: flex; justify-content: space-between; align-items: center; gap: 12px; }
  .form-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
  .form-grid.three { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .field { display: grid; gap: 6px; font-size: 12.5px; font-weight: 600; color: var(--tym-navy); }
  .field.wide { grid-column: 1 / -1; }
  .bulk { display: grid; gap: 12px; padding: 16px 20px; border-radius: var(--tym-radius-lg); }
  .bulk-head { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; }
  .bulk-btn { min-height: 36px; padding: 6px 14px; border-radius: 999px; border: 1px solid rgba(255, 255, 255, .25); color: var(--tym-white); font-size: 13px; }
  .bulk-btn:hover:not(:disabled), .bulk-btn[aria-pressed='true'] { background: var(--tym-lime); border-color: var(--tym-lime); color: var(--tym-navy); }
  .bulk-form { display: grid; grid-template-columns: minmax(0, 260px) minmax(0, 1fr) auto; gap: 10px; align-items: start; }
  .bulk-form .input { color: var(--tym-heavy-carbon); }
  .bulk-label { display: grid; gap: 6px; font-size: 12px; }
  .list { display: grid; }
  .row { display: grid; grid-template-columns: minmax(0, 2.2fr) minmax(0, .9fr) minmax(0, 1fr) minmax(0, 1.3fr) minmax(0, .8fr) 248px; gap: 14px; padding: 13px 18px; align-items: center; border-bottom: 1px solid #f0f0ee; font-size: 13px; }
  .row:last-child { border-bottom: 0; }
  .row:not(.head):hover, .row.picked { background: #fafaf8; }
  .head { font-size: 11px; letter-spacing: .06em; text-transform: uppercase; color: var(--tym-dark-gray); font-weight: 600; background: #fbfbfa; padding-top: 11px; padding-bottom: 11px; }
  .identity { display: flex; align-items: center; gap: 10px; min-width: 0; }
  .identity input { flex: none; width: 18px; height: 18px; accent-color: var(--tym-teal); }
  .avatar { flex: none; display: inline-flex; width: 36px; height: 36px; align-items: center; justify-content: center; border-radius: 50%; background: var(--tym-teal-100); color: var(--tym-teal-600); font-family: var(--tym-font-display); font-weight: 700; font-size: 13px; }
  .name { display: grid; gap: 2px; min-width: 0; text-align: left; }
  .name strong { color: var(--tym-navy); font-size: 14px; }
  .name:hover strong { text-decoration: underline; }
  .name small { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--tym-dark-gray); font-size: 12px; }
  .name .type { color: var(--tym-teal); }
  .cell { min-width: 0; }
  .muted { color: var(--tym-dark-gray); }
  .next-title { margin-bottom: 4px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--tym-heavy-carbon); }
  .actions { justify-content: flex-end; }
  .log { background: var(--tym-lime); border-color: var(--tym-lime); font-weight: 600; }
  .log:hover:not(:disabled) { background: var(--tym-lime-600); }
  .menu { position: relative; }
  .menu summary { list-style: none; cursor: pointer; }
  .menu summary::-webkit-details-marker { display: none; }
  .menu-list { position: absolute; right: 0; top: calc(100% + 6px); z-index: 10; display: grid; min-width: 180px; padding: 6px; border-radius: var(--tym-radius-md); background: var(--tym-white); box-shadow: var(--tym-shadow-3); }
  .menu-list button { text-align: left; padding: 9px 12px; border-radius: var(--tym-radius-sm); font-size: 13px; color: var(--tym-navy); }
  .menu-list button:hover { background: var(--tym-light-carbon); }
  .menu-list .danger { color: var(--tym-danger); }
  .empty { display: grid; gap: 12px; justify-items: center; padding: 40px 16px; text-align: center; }
  @media (max-width: 1100px) {
    .form-grid.three { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  }
  @media (max-width: 860px) {
    .head { display: none; }
    .row { grid-template-columns: 1fr 1fr; gap: 10px 14px; }
    .identity, .actions { grid-column: 1 / -1; }
    .actions { justify-content: flex-start; }
    .cell::before { content: attr(data-label); display: block; font-size: 11px; color: var(--tym-dark-gray); margin-bottom: 2px; }
    .form-grid, .form-grid.three, .bulk-form { grid-template-columns: 1fr; }
    .search { flex-wrap: wrap; }
  }
</style>
